import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
// eslint-disable-next-line import/no-cycle -- Ignoring all legacy import cycles
import { RequestType } from "@features/app-details/RecentRequestsView";
import { ONE_DAY_MS } from "@features/common/DateUtils";
import { RootState } from "../redux/root";

export type UnreadRequestType = Exclude<RequestType, RequestType.ALL>;

export function useRequestLastViewedTime(
  appId: string,
  requestType: UnreadRequestType,
): [number | undefined, (timestamp: number) => void] {
  const user = useSelector((state: RootState) => state.users.currentUser.value);
  const userId = user && user.id;
  const [time, setTime] = useState(
    userId != null
      ? getRequestLastViewedTime(userId, appId, requestType)
      : undefined,
  );
  useEffect(() => {
    if (userId != null) {
      setTime(getRequestLastViewedTime(userId, appId, requestType));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, [userId]);
  const setAndSaveTime = (timestamp: number) => {
    if (userId != null) {
      setRequestLastViewedTime(userId, appId, requestType, timestamp);
      setTime(timestamp);
    }
  };
  return [time, setAndSaveTime];
}

function setRequestLastViewedTime(
  userId: number,
  appId: string,
  requestType: UnreadRequestType,
  timestamp: number,
): void {
  localStorage.setItem(
    getLastViewedTimeKey(userId, appId, requestType),
    timestamp.toString(),
  );
}

function getRequestLastViewedTime(
  userId: number,
  appId: string,
  requestType: UnreadRequestType,
): number | undefined {
  const timestampString = localStorage.getItem(
    getLastViewedTimeKey(userId, appId, requestType),
  );
  if (!timestampString) {
    return undefined;
  }
  const result = +timestampString;
  return isNaN(result) ? undefined : result;
}

function getLastViewedTimeKey(
  userId: number,
  appId: string,
  requestType: UnreadRequestType,
): string {
  return `alchemyapi.lastViewedRequestTime.${userId}.${appId}.${requestType}`;
}

export enum ExpirableEvent {
  USAGE_WARNING_BANNER_DISMISSED = "usageWarningBannerDismissed",
  USAGE_DANGER_BANNER_DISMISSED = "usageDangerBannerDismissed",
  USAGE_LIMIT_BANNER_DISMISSED = "usageLimitBannerDismissed",
}

function getExpirableEventKey(
  userId: number | undefined,
  event: ExpirableEvent,
): string {
  // eslint-disable-next-line @typescript-eslint/restrict-template-expressions -- FIXME
  return `alchemyapi.expirableEvent.${userId}.${event}`;
}

export function useExpirableEventStorage(
  event: ExpirableEvent,
  // session storage is cleared when the tab is closed, but for situation where user
  // leaves their tab forever, we will fall back to the expiration time
  timeToLive = ONE_DAY_MS,
  storageType: "session" | "local" = "local",
): [boolean, () => void] {
  const storage = storageType === "session" ? sessionStorage : localStorage;
  const userId = useSelector(
    (state: RootState) => state.users.currentUser.value?.id,
  );
  const key = getExpirableEventKey(userId, event);
  const value = { expiration: new Date().getTime() + timeToLive };
  const [isEventSet, setIsEventSet] = useState(
    userId ? isEventRegistered(storage, key) : true,
  );
  useEffect(() => {
    if (userId) {
      setIsEventSet(isEventRegistered(storage, key));
    }
  }, [storage, userId, key]);
  const setEvent = () => {
    setIsEventSet(true);
    storage.setItem(key, JSON.stringify(value));
  };

  return [isEventSet, setEvent];
}

function isEventRegistered(storage: Storage, key: string): boolean {
  const valueStr = storage.getItem(key);
  if (!valueStr) {
    return false;
  }
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- FIXME
  const item = JSON.parse(valueStr);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- FIXME
  if (new Date().getTime() > item.expiration) {
    storage.removeItem(key);
    return false;
  }
  return true;
}

export enum OneTimeEvent {
  AlertsNewNav = "alertsNewNav",
  HomeRecommendedProduct = "homeRecommendedProduct",
  AlertsAnnouncement = "alertsAnnouncement",
}

function getOneTimeEventKey(
  userId: number | undefined,
  event?: OneTimeEvent,
): string | null {
  if (!userId || !event) return null;

  return `alchemyapi.oneTimeEvent.${userId}.${event}`;
}

export function useOneTimeEventStorage(
  event?: OneTimeEvent,
): [boolean, () => void] {
  const userId = useSelector(
    (state: RootState) => state.users.currentUser.value?.id,
  );
  const key = getOneTimeEventKey(userId, event);
  const [hasEventOccured, setHasEventOccured] = useState(true);
  useEffect(() => {
    if (userId && key) {
      setHasEventOccured(hasEventHappened(key));
    }
  }, [userId, key]);
  const setEventOccured = useCallback(() => {
    if (!hasEventOccured && key) {
      setHasEventOccured(true);
      const value = { eventOccured: true };
      localStorage.setItem(key, JSON.stringify(value));
    }
  }, [hasEventOccured, key]);
  return [hasEventOccured, setEventOccured];
}

function hasEventHappened(key: string): boolean {
  return !!localStorage.getItem(key);
}
